import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  CircularProgress,
  Alert,
  MenuItem,
} from '@mui/material';
import { useNotify, useGetIdentity, useGetOne, useGetList } from 'react-admin';
import { getToken } from '../../authentication/authProvider';

const PAPERMAN_API_URL = process.env.REACT_APP_PAPERMAN_API_URL;

// Select value for "don't print a marital status".
const LEAVE_OFF = '__leave_off__';

// "Divorcé/Divorced" → "Divorced": staff read the dialog in English.
const englishHalf = (name?: string | null): string => {
  const halves = String(name ?? '').split(/\s*\/\s*/).filter(Boolean);
  return halves.length > 1 ? halves[halves.length - 1] : String(name ?? '');
};

interface ReissueDocumentDialogProps {
  open: boolean;
  onClose: () => void;
  document: any;
  onSuccess: () => void;
  region?: string;
  funeral?: any;
}

export function ReissueDocumentDialog({ open, onClose, document, onSuccess, region, funeral }: ReissueDocumentDialogProps) {
  const notify = useNotify();
  const { identity } = useGetIdentity();
  const deceasedClientId = funeral?.deceased_client_id;
  const { data: deceasedClient } = useGetOne('clients', { id: deceasedClientId }, { enabled: !!deceasedClientId });
  const [loading, setLoading] = useState(false);
  const [reissueNote, setReissueNote] = useState('');
  const [issuerName, setIssuerName] = useState('');
  const [issuerTitle, setIssuerTitle] = useState('Funeral Director');
  const [deceasedName, setDeceasedName] = useState('');
  const [dateOfDeath, setDateOfDeath] = useState('');
  const [placeOfDeath, setPlaceOfDeath] = useState('');
  const [dateOfBirth, setDateOfBirth] = useState('');
  const [cremationDate, setCremationDate] = useState('');
  const [cremationFacility, setCremationFacility] = useState('');
  const [maritalStatus, setMaritalStatus] = useState<string>(LEAVE_OFF);
  const [declarantRelationship, setDeclarantRelationship] = useState('');
  const isPod = document?.document_type === 'POD';
  const { data: maritalStatuses } = useGetList(
    'marital_statuses',
    { pagination: { page: 1, perPage: 50 }, sort: { field: 'id', order: 'ASC' } },
    { enabled: open && isPod },
  );

  useEffect(() => {
    if (open) {
      const r = (region || '').toLowerCase();
      setIssuerName(r === 'ontario' ? 'Megan Zavitz' : 'Bram Paperman');
      setReissueNote('');
      // Pre-fill from CURRENT (live) funeral + deceased client data, falling back
      // to the original document snapshot only when no live value exists. Reissue
      // exists to pick up corrections (e.g. a fixed birthdate), so the snapshot
      // must never override live data.
      const payload = document?.rendered_payload_json;
      const currentClientName = deceasedClient
        ? ([deceasedClient.first_name, deceasedClient.middle_name, deceasedClient.last_name].filter(Boolean).join(' ').trim()
          || deceasedClient.name || '')
        : '';
      // Keep a name staff extended on the original (middle names added by hand)
      // when it still starts and ends with the live first and last name —
      // otherwise every reissue dropped them again (case 721 retyped one twice).
      const snapshotName = String(payload?.deceased_full_name || '').trim();
      const lower = (v: unknown) => String(v || '').trim().toLowerCase();
      const extendsLiveName =
        !!snapshotName && !!deceasedClient?.first_name && !!deceasedClient?.last_name
        && lower(snapshotName).startsWith(lower(deceasedClient.first_name))
        && lower(snapshotName).endsWith(lower(deceasedClient.last_name))
        && snapshotName.length > currentClientName.length;
      setDeceasedName((extendsLiveName ? snapshotName : currentClientName) || snapshotName || '');

      const liveDateOfDeath = funeral?.date_time_of_death
        ? new Date(funeral.date_time_of_death).toLocaleDateString('en-CA', { timeZone: 'America/New_York' })
        : (payload?.date_of_death ? String(payload.date_of_death).split('T')[0] : '');
      setDateOfDeath(liveDateOfDeath);

      // Place of death, marital status and the declarant's relationship are what
      // staff curated on the original, not live case data (funeral.place_of_death
      // is only the facility name), so they start from the snapshot.
      setPlaceOfDeath(payload?.place_of_death || funeral?.place_of_death || '');
      setMaritalStatus(payload?.marital_status || LEAVE_OFF);
      setDeclarantRelationship(payload?.declarant_relationship || '');

      const liveDob = deceasedClient?.birthdate || deceasedClient?.date_of_birth || payload?.date_of_birth;
      setDateOfBirth(liveDob ? String(liveDob).split('T')[0] : '');

      const liveCremationDate = funeral?.cremation_date || payload?.cremation_date;
      setCremationDate(liveCremationDate ? String(liveCremationDate).split('T')[0] : '');

      // No live DB source for cremation facility — the snapshot is the source of truth.
      setCremationFacility(payload?.cremation_facility || '');
    }
  }, [open, region, document, deceasedClient, funeral]);

  const handleReissue = async () => {
    if (!document) return;
    if (!reissueNote.trim()) {
      notify('Please provide a reissue note explaining the correction', { type: 'error' });
      return;
    }

    setLoading(true);
    try {
      const token = await getToken();
      const response = await fetch(`${PAPERMAN_API_URL}documents/${document.id}/reissue`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          reissue_note: reissueNote,
          issuer_name: issuerName,
          issuer_title: issuerTitle,
          deceased_full_name: deceasedName || undefined,
          date_of_death: dateOfDeath || undefined,
          place_of_death: placeOfDeath || undefined,
          date_of_birth: dateOfBirth || undefined,
          cremation_date: cremationDate || undefined,
          cremation_facility: cremationFacility || undefined,
          ...(isPod
            ? {
                marital_status: maritalStatus === LEAVE_OFF ? null : maritalStatus,
                declarant_relationship: declarantRelationship.trim() || null,
              }
            : {}),
        }),
      });

      if (!response.ok) {
        const err = await response.json();
        throw new Error(err.message || 'Failed to reissue');
      }

      const result = await response.json();
      notify(`New version issued: ${result.verification_id}`, { type: 'success' });
      onSuccess();
      onClose();
    } catch (e: any) {
      notify(e.message || 'Failed to reissue document', { type: 'error' });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Reissue Corrected Version</DialogTitle>
      <DialogContent>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16, marginTop: 8 }}>
          <Alert severity="info" sx={{ fontSize: '12px' }}>
            This will supersede <strong>{document?.verification_id}</strong> and issue a new version. The previous version will be marked as superseded.
          </Alert>

          <TextField
            label="Reissue Note (internal only)"
            value={reissueNote}
            onChange={e => setReissueNote(e.target.value)}
            fullWidth
            required
            multiline
            minRows={2}
            placeholder="Explain what was corrected..."
          />

          <TextField
            label="Deceased Full Name"
            value={deceasedName}
            onChange={e => setDeceasedName(e.target.value)}
            fullWidth
          />

          <TextField
            label="Date of Death"
            type="date"
            value={dateOfDeath}
            onChange={e => setDateOfDeath(e.target.value)}
            fullWidth
            InputLabelProps={{ shrink: true }}
          />

          <TextField
            label="Date of Birth"
            type="date"
            value={dateOfBirth}
            onChange={e => setDateOfBirth(e.target.value)}
            fullWidth
            InputLabelProps={{ shrink: true }}
          />

          <TextField
            label="Place of Death"
            value={placeOfDeath}
            onChange={e => setPlaceOfDeath(e.target.value)}
            fullWidth
          />

          {isPod && (
            <>
              <TextField
                select
                label="Marital Status"
                value={maritalStatus}
                onChange={e => setMaritalStatus(e.target.value)}
                fullWidth
                helperText="Only changes this document — the deceased's record is not edited."
              >
                <MenuItem value={LEAVE_OFF}>Leave it off the document</MenuItem>
                {(maritalStatuses || []).map((m: any) => (
                  <MenuItem key={m.id} value={m.name}>{englishHalf(m.name)}</MenuItem>
                ))}
              </TextField>
              <TextField
                label="Declarant Relationship"
                value={declarantRelationship}
                onChange={e => setDeclarantRelationship(e.target.value)}
                fullWidth
                helperText='e.g. "Wife / Executor"'
              />
            </>
          )}

          {document?.document_type === 'POC' && (
            <>
              <TextField
                label="Cremation Date"
                type="date"
                value={cremationDate}
                onChange={e => setCremationDate(e.target.value)}
                fullWidth
                InputLabelProps={{ shrink: true }}
              />
              <TextField
                label="Cremation Facility"
                value={cremationFacility}
                onChange={e => setCremationFacility(e.target.value)}
                fullWidth
              />
            </>
          )}

          <TextField
            label="Issuer Name"
            value={issuerName}
            onChange={e => setIssuerName(e.target.value)}
            fullWidth
            required
          />

          <TextField
            label="Issuer Title"
            value={issuerTitle}
            onChange={e => setIssuerTitle(e.target.value)}
            fullWidth
            required
          />
        </div>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={loading}>Cancel</Button>
        <Button onClick={handleReissue} variant="contained" disabled={loading}>
          {loading ? <CircularProgress size={20} /> : 'Reissue'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
